import { BaseDragger } from "./BaseDragger";
import { DraggerType, type Point2D, type Point3D } from "./types";
import { selectionService } from "../selection";
import { selectConfig } from "./selectConfig";
import { deleteObj } from "../core/delete";
import { Mode, SubentSelectionMode } from "../core/constants";
import {
  DEFAULT_WINDOW_SELECTION_STYLE,
  DEFAULT_CROSSING_SELECTION_STYLE,
} from "../overlay/types";
import { Logger } from "@/logger";
import type { DrawingModule } from "../types/DrawingWeb";

/**
 * Minimum drag distance to trigger window/crossing selection (in pixels).
 */
const MIN_DRAG_DISTANCE = 10;

/**
 * Half-size of the selection aperture for point selection (in pixels) — the
 * hit box is twice this wide. Using a small window instead of a true point
 * for better usability. Several entities can touch the box: only the topmost
 * one (draw order) is kept, so 1 click = 1 entity.
 */
const POINT_SELECTION_APERTURE = 8;

/**
 * SelectDragger - GPU-accelerated entity selection using native ODA APIs.
 *
 * Uses OdDbSelectionSet.select1() for GPU selection and
 * OdDbEntity.highlight() for visual feedback.
 *
 * Selection modes:
 * - Point: Click to select the topmost entity under the cursor (Mode.kPoint)
 * - Window: Drag left-to-right to select entities entirely within (Mode.kWindow)
 * - Crossing: Drag right-to-left to select entities intersecting (Mode.kCrossing)
 *
 * Each selection replaces the previous one, unless Shift is held or the
 * additive mode is enabled (selectConfig.additive), in which case it is added.
 *
 * Transform handles are managed separately via the overlay system.
 */
export class SelectDragger extends BaseDragger {
  readonly type = DraggerType.Select;

  /** Preview overlay ID for window/crossing rectangle */
  private windowPreviewId: string | null = null;

  // ============== Dragger Callbacks ==============

  protected onStart(): void {
    this.clearWindowPreview();
  }

  protected onDrag(): void {
    if (!this.startScreen || !this.currentScreen) return;

    const dx = this.currentScreen.x - this.startScreen.x;
    const dy = this.currentScreen.y - this.startScreen.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Show window preview if drag distance exceeds threshold
    if (distance >= MIN_DRAG_DISTANCE) {
      this.updateWindowPreview();
    }
  }

  protected onEnd(screen: Point2D, world: Point3D, ev: PointerEvent): void {
    if (
      !this.startScreen ||
      !this.currentScreen ||
      !this.startWorld ||
      !this.currentWorld
    ) {
      console.warn("[SelectDragger] Missing coordinates in onEnd");
      this.clearWindowPreview();
      this.clear();
      return;
    }

    const dx = this.currentScreen.x - this.startScreen.x;
    const dy = this.currentScreen.y - this.startScreen.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Determine selection mode
    let mode: number;
    if (distance < MIN_DRAG_DISTANCE) {
      mode = Mode.kPoint;
    } else if (dx >= 0) {
      mode = Mode.kWindow; // Left to right
    } else {
      mode = Mode.kCrossing; // Right to left
    }

    // Shift held (or the additive mode toggled from the toolbar) = additive selection.
    const additive = ev.shiftKey || selectConfig.additive;

    // Perform selection using native ODA API
    this.performSelection(mode, additive);

    // Update view
    this.viewer.update();

    // Clear preview and drag state
    this.clearWindowPreview();
    this.clear();
  }

  protected onCancel(): void {
    this.clearWindowPreview();
    this.clear();
  }

  // ============== Native Selection ==============

  /**
   * Perform selection using OdDbSelectionSet.select1().
   */
  private performSelection(mode: number, additive: boolean): void {
    const db = this.viewer.database;
    if (!db || !this.startWorld || !this.startScreen) {
      console.warn("[SelectDragger] No database or coordinates");
      return;
    }

    // Get active viewport ID
    const vpId = db.activeViewportId();
    if (!vpId || vpId.isNull()) {
      Logger.warn("[SelectDragger] No active viewport ID");
      return;
    }

    // Create point array for selection
    const pointArray = new this.module.OdGePoint3dArray();

    const isPointPick = mode === Mode.kPoint;
    if (isPointPick) {
      // Point selection - use a small crossing window for better usability
      // ODA's kPoint mode can have zero tolerance, making it hard to select entities
      // Instead, we create a small selection aperture around the click point
      const aperture = this.viewer.screenDistanceToWorld(
        POINT_SELECTION_APERTURE,
      );
      if (aperture <= 0) {
        deleteObj(pointArray);
        return;
      }

      const pt1 = new this.module.OdGePoint3d(
        this.startWorld.x - aperture,
        this.startWorld.y - aperture,
        0,
      );
      const pt2 = new this.module.OdGePoint3d(
        this.startWorld.x + aperture,
        this.startWorld.y + aperture,
        0,
      );
      pointArray.push_back(pt1);
      pointArray.push_back(pt2);
      deleteObj(pt1);
      deleteObj(pt2);

      // Use crossing mode for point selection (selects anything touching the aperture)
      mode = Mode.kCrossing;
    } else {
      // For drag selection, skip if world coordinates are identical (degenerate box → ODA crash)
      if (
        this.startWorld.x === this.currentWorld?.x &&
        this.startWorld.y === this.currentWorld?.y
      ) {
        deleteObj(pointArray);
        return;
      }
      // Window/Crossing selection - two corner points
      const pt1 = new this.module.OdGePoint3d(
        this.startWorld.x,
        this.startWorld.y,
        0,
      );
      const pt2 = new this.module.OdGePoint3d(
        this.currentWorld!.x,
        this.currentWorld!.y,
        0,
      );
      pointArray.push_back(pt1);
      pointArray.push_back(pt2);
      deleteObj(pt1);
      deleteObj(pt2);
    }

    // Perform GPU selection
    const nPoints = pointArray.size();
    const modeLabel =
      mode === Mode.kWindow
        ? "kWindow"
        : mode === Mode.kCrossing
          ? "kCrossing"
          : `unknown(${mode})`;

    // Dehighlight the current selection BEFORE select1 runs its GPU hit-test.
    // select1 crashes with "null function" when the hit-test region overlaps an
    // already-highlighted entity that was just transformed/duplicated (its
    // highlighted render state can be stale). setFromNative()/addFromNative()
    // would otherwise only dehighlight AFTER select1 has already run.
    selectionService.highlightAll(false);

    let newSelection: DrawingModule.OdDbSelectionSet | null = null;
    try {
      newSelection = this.module.OdDbSelectionSet.select1(
        vpId,
        nPoints,
        pointArray.asArrayPtr(),
        mode,
        SubentSelectionMode.kDisableSubents,
        null as any,
      );
    } catch (error) {
      Logger.captureException(error, {
        mode: modeLabel,
        nPoints,
        startScreen: this.startScreen,
        currentScreen: this.currentScreen,
        startWorld: this.startWorld,
        currentWorld: this.currentWorld,
        vpIdIsNull: vpId.isNull(),
      });
      deleteObj(pointArray);
      return;
    }

    deleteObj(pointArray);

    // Handle additive vs replace selection (a click keeps the topmost entity only)
    if (additive && newSelection) {
      selectionService.addFromNative(newSelection, isPointPick);
    } else {
      selectionService.setFromNative(newSelection, isPointPick);
    }
  }

  // ============== Preview Rendering ==============

  /**
   * Update the selection window preview.
   */
  private updateWindowPreview(): void {
    if (!this.startScreen || !this.currentScreen) return;

    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    const dx = this.currentScreen.x - this.startScreen.x;
    const isWindow = dx >= 0;
    const style = isWindow
      ? DEFAULT_WINDOW_SELECTION_STYLE
      : DEFAULT_CROSSING_SELECTION_STYLE;

    const x = Math.min(this.startScreen.x, this.currentScreen.x);
    const y = Math.min(this.startScreen.y, this.currentScreen.y);
    const width = Math.abs(dx);
    const height = Math.abs(this.currentScreen.y - this.startScreen.y);

    if (this.windowPreviewId) {
      overlay.update(this.windowPreviewId, {
        x,
        y,
        width,
        height,
        style,
      });
    } else {
      this.windowPreviewId = overlay.createRectangle(
        x,
        y,
        width,
        height,
        style,
      );
    }
  }

  /**
   * Clear the selection window preview.
   */
  private clearWindowPreview(): void {
    if (this.windowPreviewId && this.viewer.overlayManager) {
      this.viewer.overlayManager.remove(this.windowPreviewId);
      this.windowPreviewId = null;
    }
  }

  // ============== Public API ==============

  /**
   * Clear the selection.
   */
  clearSelection(): void {
    selectionService.clear();
    this.viewer.update();
  }

  // ============== Lifecycle ==============

  protected onDeactivate(): void {
    this.clearWindowPreview();
  }

  dispose(): void {
    this.clearWindowPreview();
    super.dispose();
  }
}
